import React from 'react';
import type { BreadcrumbProps } from 'antd';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';

const TOP_LEVEL_TITLES: Record<string, string> = {
  awards: 'Awards',
  chat: 'Chat',
  membership: 'Membership',
  'member-listing': 'Members',
  magazines: 'Magazines',
  events: 'Events',
};

const AWARDS_SECTION_TITLES: Record<string, string> = {
  projects: 'Projects Awards',
  other: 'Other Awards',
  photos: 'Event Photos',
  magazine: 'Benchmark Magazine',
};

const GIS_SECTION_TITLES: Record<string, string> = {
  gis_faq: 'GIS FAQ',
  gis_policy: 'GIS Policies and Procedures',
  guide_specifications: 'Guide Specifications',
  gis_wording: 'Specify the GIS',
  gis_fees: 'GIS Fees',
  gis_for_manufacturers: 'Manufacturer Templates',
  'gis-estimator': 'GIS Fee Estimator',
};

const decode = (s: string) => {
  try { return decodeURIComponent(s); } catch { return s; }
};

const item = (title: string, to?: string) => ({ title: to ? <Link to={to}>{title}</Link> : <span>{title}</span> });

export const Breadcrumbs: React.FC = () => {
  const { pathname } = useLocation();
  if (pathname === '/') return null;

  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return null;

  const [first, second] = parts;

  const items: BreadcrumbProps['items'] = [];

  items.push({
    title: (
        <Link to="/">
          <HomeOutlined style={{ marginRight: 5 }} />
          Home
        </Link>
    ),
  });

  if (first === 'events' && second) {
    items.push(item('Awards', '/awards/projects'));
    items.push(item(AWARDS_SECTION_TITLES.photos, '/awards/photos'));
    items.push(item(`Event ${decode(second)}`));
    return <div style={{ position: 'relative' }} aria-label="breadcrumb"><Breadcrumb items={items} /></div>;
  }

  // /magazines -> Home / Awards / Benchmark Magazine
  if (first === 'magazines' && !second) {
    items.push(item('Awards', '/awards/projects'));
    items.push(item(AWARDS_SECTION_TITLES.magazine));
    return <div style={{ position: 'relative' }} aria-label="breadcrumb"><Breadcrumb items={items} /></div>;
  }

  if (first === 'magazines' && second) {
    items.push(item('Awards', '/awards/projects'));
    items.push(item(AWARDS_SECTION_TITLES.magazine, '/awards/magazine'));
    items.push(item(`${decode(second)}`));
    return <div style={{ position: 'relative' }} aria-label="breadcrumb"><Breadcrumb items={items} /></div>;
  }

  // /awards or /awards/:section -> Home / Awards [/ Section]
  if (first === 'awards') {
    items.push(item('Awards', '/awards/projects'));
    if (second) {
      const sectionTitle = AWARDS_SECTION_TITLES[second] || decode(second);
      items.push(item(sectionTitle));
    }
    return <div style={{ position: 'relative' }} aria-label="breadcrumb"><Breadcrumb items={items} /></div>;
  }

  if (first === 'gis') {
    items.push(item('GIS', '/gis'));

    if (second) {
      items.push(item(GIS_SECTION_TITLES[second] || decode(second)));
    }

    return (
        <div style={{ position: 'relative' }} aria-label="breadcrumb">
          <Breadcrumb items={items} />
        </div>
    );
  }

  items.push(item(TOP_LEVEL_TITLES[first] || decode(first), `/${first}`));
  if (second) items.push(item(decode(second)));

  return (
      <div style={{ position: 'relative' }} aria-label="breadcrumb">
        <Breadcrumb items={items} />
      </div>
  );
};
